// Stamen Watercolor, served by the Cooper Hewitt (Smithsonian Design Museum),
// which holds the full tile archive in its permanent collection and invites
// developers to "use the tiles from our servers in independent projects".
//
// The tiles are fetched by the browser through plain <img> elements, which is
// the ONLY way they can be consumed: the museum answers 404 to requests coming
// from cloud IP ranges (verified against CloudFront on 2026-08-28), so no
// server-side proxy or CDN can sit in front of it. Leaflet needs none.
//
// Cache-Control on the tiles is ten years, so each visitor downloads a tile once.
export const WATERCOLOR_TILE_URL =
  'https://watercolormaps.collection.cooperhewitt.org/tile/watercolor/{z}/{x}/{y}.jpg'

export const WATERCOLOR_ATTRIBUTION =
  'Map tiles by <a href="https://stamen.com">Stamen Design</a>, ' +
  '<a href="https://creativecommons.org/licenses/by/3.0/">CC BY 3.0</a>, hosted by ' +
  '<a href="https://watercolormaps.collection.cooperhewitt.org">Cooper Hewitt</a> &middot; ' +
  'Data &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'

// Verified 2026-08-28: the archive answers z0 through z18. We stop at 16: past
// it the watercolor dissolves into blobs and every pin is already on its own.
// The floor is 3: below it the world repeats and the clusters stack on top
// of their own copies.
export const WATERCOLOR_MIN_ZOOM = 3
export const WATERCOLOR_MAX_ZOOM = 16

// Watercolor is a light map; while tiles load, paper beats a gray void.
export const WATERCOLOR_BACKGROUND = '#e9e4d6'

// ---- street labels ----------------------------------------------------
//
// The watercolor archive was rendered label-free by design: it carries no
// street names, no neighbourhood names, nothing to navigate by. Beautiful, and
// the reason visitors called the map "kind of unusable" for finding their way
// to a piece. The fix is the standard raster stack -- a transparent labels-only
// layer drawn on top of the painting.
//
// CARTO's light_only_labels is the label half of their Positron style served on
// its own, with everything else transparent.
//
// Verified 2026-10-05: CARTO now gates this endpoint. A request with no key
// still answers 200, but the body is a fixed "API KEY REQUIRED" placeholder
// image (same byte size regardless of the tile requested) instead of the real
// labels -- it reads as working until you look at the pixels, which is how
// this broke silently in production. The key below is a public, client-side
// basemap key (restricted by domain in the CARTO account), the same kind of
// credential as the Google Maps key in hello_vue.js, and is committed for the
// same reason. The watercolor tiles above are unaffected: Cooper Hewitt serves
// its own ten-year-cached static archive, independent of CARTO's live infra.
//
// {r} WITHOUT detectRetina, which reads like a mistake and is not. In Leaflet
// 1.9 the two are independent despite the docs pairing them: getTileUrl fills
// {r} from Browser.retina alone (`r: Browser.retina ? '@2x' : ''`), while
// detectRetina is a separate, heavier switch that halves tileSize and does
// `maxZoom = maxZoom - 1`. Turning it on would stop the labels one zoom short
// of WATERCOLOR_MAX_ZOOM -- exactly the zoom where street names matter most.
// Left off, {r} still serves @2x tiles to retina screens at every zoom, which
// is all we wanted from it. Text is the one thing on this map that has to
// survive close reading, so the sharper glyphs are worth the heavier tile.
//
// Verified 2026-10-05: with the key, CARTO answers 200 with real label tiles
// (checked against the @2x variant at z3, z8 and z15).
export const LABELS_TILE_URL =
  'https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png?key=cb1_4510_1_0c63fa088e0a9090de1c046b'

// Verified 2026-09-03: a, b, c and d all answer 200 image/png.
export const LABELS_SUBDOMAINS = 'abcd'

// Required by CARTO's terms for the free basemap service. Leaflet appends it to
// the attribution control next to the watercolor credit.
export const LABELS_ATTRIBUTION =
  '&copy; <a href="https://carto.com/attributions">CARTO</a>'

// The labels are drawn full strength by default, which reads as a second map
// pasted over the painting. Knocking them back lets the watercolor through
// while the glyphs stay legible.
//
// Opacity and NOT mix-blend-mode: multiply, which is the usual trick for
// blending a label layer. light_only_labels ships a white halo around every
// glyph, and that halo is the only thing keeping the text readable over
// irregular painted colour. multiply renders white as transparent, so it eats
// the halo and makes legibility worse, not better.
export const LABELS_OPACITY = 0.85

// Labels live in their own Leaflet pane so this one number decides where they
// sit in the stack. Leaflet's own panes are tile 200, overlay 400, shadow 500,
// marker 600 (leaflet.css). 350 puts the labels above the watercolor tiles --
// the whole point -- and below everything a visitor interacts with, so a street
// name never draws over an art pin, a cluster bubble or a popup.
export const LABELS_PANE = 'labels'
export const LABELS_PANE_Z_INDEX = 350

// ---- the visitor's own position ---------------------------------------
//
// Its own pane for the same reason the labels have one: the stacking order is
// decided once, here, by a single number. The gap this has to land in is
// narrow. Leaflet's panes are tile 200, overlay 400, shadow 500, marker 600
// (leaflet.css), and LABELS_PANE_Z_INDEX above adds 350.
//
// 450 is above the labels, so a street name never crosses out the dot that is
// supposed to be orienting the visitor, and above overlayPane too, so nothing
// drawn as a plain overlay later -- a boundary, a heat layer -- can bury it.
// It is below markerPane, which is the decision that matters: the art is the
// content of this map and the dot is only orientation, so a pin always wins the
// pixel. A visitor who cannot click the mural because their own location is
// sitting on top of it has been handed a worse map, not a better one.
export const USER_LOCATION_PANE = 'user-location'
export const USER_LOCATION_PANE_Z_INDEX = 450

// Blue, and specifically not either of this map's own colours. #fe0205 is the
// ring around every art pin and #12302e is the site's chrome; a dot in either
// reads as one more piece of art rather than as "you". Blue is what every map
// product has trained people to read as their own position, and it is the one
// hue on this map that means nothing else yet.
export const USER_LOCATION_COLOR = '#1a73e8'

// Pixels, and the reason the dot is an L.circleMarker rather than an L.circle:
// a circleMarker's radius is screen-space, so the dot stays the same size at
// every zoom. "You" is not a thing that has a diameter in metres.
export const USER_LOCATION_DOT_RADIUS = 7

// The white ring is what makes the dot survive watercolor. The basemap is a
// painting -- the colour under the dot changes every time the map moves -- so a
// flat blue disc disappears the moment it lands on blue water. A white stroke
// separates it from whatever is behind it, which is the same trick CARTO's
// label tiles use with their glyph halo (see LABELS_OPACITY).
export const USER_LOCATION_DOT_STYLE = Object.freeze({
  radius: USER_LOCATION_DOT_RADIUS,
  color: '#ffffff',
  weight: 3,
  opacity: 1,
  fillColor: USER_LOCATION_COLOR,
  fillOpacity: 1,
  // The dot is an indicator, not a target: clicks belong to the map underneath
  // (which closes the detail panel) and to any art pin it happens to cover.
  interactive: false,
})

// The accuracy ring is an L.circle, whose radius IS in metres, because that is
// the entire claim it makes: "the device says you are somewhere inside this
// many metres". It therefore has to grow and shrink with zoom -- a ring that
// stayed 40px wide would be asserting a different accuracy at every zoom level.
//
// Very low fill opacity on purpose. In dense downtown the ring can swallow
// several blocks of pins, and a heavier fill would tint the art underneath it.
export const USER_LOCATION_ACCURACY_STYLE = Object.freeze({
  color: USER_LOCATION_COLOR,
  weight: 1,
  opacity: 0.4,
  fillColor: USER_LOCATION_COLOR,
  fillOpacity: 0.12,
  interactive: false,
})

// Where the map lands on the first fix. Close enough to see the individual
// pins around the visitor -- at the coverage zooms they are still clustered
// bubbles, which answers "what is near me" with a number instead of with art.
// Capped by, not equal to, WATERCOLOR_MAX_ZOOM: 16 is the last zoom the tiles
// exist at, and arriving pinned to the very edge of the basemap leaves no room
// to look around. Only ever used to zoom IN: a visitor already closer than this
// keeps their zoom (see the setView call in MapComponent).
export const USER_LOCATION_ZOOM = 15
